// packages/ui/src/components/security/ActiveSessionsTab.tsx
//
// UAT-2 (2026-05-16) — Active Sessions tab inside Security Center.
// Wires GET /api/v1/auth/me/sessions for the list and
// DELETE /api/v1/auth/me/sessions/:id for revoke.

import { useCallback, useEffect, useState, type JSX } from 'react';

import { Button } from '@printway-global/design-system';

// Sandbox/prod: the SPA host (vendor-v3.printway.work) has no /api proxy —
// requests to a relative /api path return the SPA index.html. Mirror the
// pattern in csrfFetch: prefix with VITE_API_BASE_URL when present.
const API_BASE = (
  (
    (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env
      ?.VITE_API_BASE_URL ?? ''
  ) as string
).replace(/\/$/, '');

function apiUrl(path: string): string {
  if (!path.startsWith('/api/')) return path;
  return API_BASE ? `${API_BASE}${path}` : path;
}

function readXsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  for (const part of raw.split('; ')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq) === 'XSRF-TOKEN') {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return part.slice(eq + 1);
      }
    }
  }
  return null;
}

interface SessionRow {
  sessionId: string;
  ip: string;
  userAgent: string;
  createdAt: string;
  lastSeen: string;
  isCurrent: boolean;
}

interface SessionsListResponse {
  success: boolean;
  data?: { sessions: SessionRow[] };
  error?: { code: string; message: string };
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('vi-VN', { hour12: false });
}

function uaSummary(ua: string): string {
  if (!ua) return 'Trình duyệt không xác định';
  // Heuristic short summary — full UA in hover title.
  const browser = (() => {
    if (/Edg\//.test(ua)) return 'Edge';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua)) return 'Safari';
    return 'Trình duyệt khác';
  })();
  const os = (() => {
    if (/Windows NT/.test(ua)) return 'Windows';
    if (/Mac OS X/.test(ua)) return 'macOS';
    if (/Android/.test(ua)) return 'Android';
    if (/iPhone|iPad/.test(ua)) return 'iOS';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Hệ điều hành khác';
  })();
  return `${browser} · ${os}`;
}

export function ActiveSessionsTab(): JSX.Element {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(apiUrl('/api/v1/auth/me/sessions'), {
        credentials: 'include',
      });
      const j = (await r.json()) as SessionsListResponse;
      if (!r.ok || !j.success || !j.data) {
        setError(j.error?.code ?? `HTTP_${r.status}`);
        return;
      }
      setRows(j.data.sessions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'NETWORK');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = useCallback(
    async (sessionId: string) => {
      setRevoking(sessionId);
      setError(null);
      try {
        const xsrf = readXsrfCookie();
        const r = await fetch(
          apiUrl(`/api/v1/auth/me/sessions/${encodeURIComponent(sessionId)}`),
          {
            method: 'DELETE',
            credentials: 'include',
            headers: xsrf ? { 'X-XSRF-TOKEN': xsrf } : undefined,
          },
        );
        if (r.status === 204) {
          await load();
          return;
        }
        const j = (await r.json().catch(() => null)) as
          | { error?: { code?: string } }
          | null;
        setError(j?.error?.code ?? `HTTP_${r.status}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'NETWORK');
      } finally {
        setRevoking(null);
      }
    },
    [load],
  );

  if (loading && rows === null) {
    return (
      <p
        data-testid="sessions-loading"
        className="text-center text-sm text-pw-text-muted"
      >
        Đang tải danh sách phiên đăng nhập…
      </p>
    );
  }

  if (error && rows === null) {
    return (
      <div className="space-y-3">
        <p
          data-testid="sessions-error"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          Không tải được danh sách phiên: {error}
        </p>
        <Button type="button" variant="secondary" onClick={() => void load()}>
          Thử lại
        </Button>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <p className="text-center text-sm text-pw-text-muted">
        Chưa có phiên đăng nhập nào ngoài phiên hiện tại.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="sessions-list">
      <p className="text-xs text-pw-text-muted">
        Đăng nhập từ thiết bị khác sẽ hiện ở đây. Bấm <strong>Đăng xuất</strong>{' '}
        để chấm dứt phiên đó từ xa.
      </p>
      {error ? (
        <p
          data-testid="sessions-error"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </p>
      ) : null}
      <ul className="divide-y divide-pw-border rounded-md border border-pw-border">
        {rows.map((s) => (
          <li
            key={s.sessionId}
            data-testid={`session-row-${s.sessionId}`}
            className="flex items-center justify-between gap-4 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-sm font-medium text-pw-text"
                title={s.userAgent}
              >
                {uaSummary(s.userAgent)}
                {s.isCurrent ? (
                  <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-green-800">
                    Phiên hiện tại
                  </span>
                ) : null}
              </p>
              <p className="mt-0.5 text-xs text-pw-text-muted">
                IP: {s.ip || '—'} · Lần cuối: {formatTime(s.lastSeen)}
              </p>
              <p className="mt-0.5 text-[10px] text-pw-text-muted">
                Bắt đầu: {formatTime(s.createdAt)} · ID:{' '}
                <code className="font-mono">{s.sessionId.slice(0, 8)}</code>
              </p>
            </div>
            {s.isCurrent ? null : (
              <Button
                type="button"
                variant="secondary"
                disabled={revoking === s.sessionId}
                onClick={() => void revoke(s.sessionId)}
                data-testid={`session-revoke-${s.sessionId}`}
              >
                {revoking === s.sessionId ? 'Đang đăng xuất…' : 'Đăng xuất'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default ActiveSessionsTab;
